import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Button,
	VStack,
	Box,
	Text,
	useToast,
	Tabs,
	TabList,
	TabPanels,
	Tab,
	TabPanel,
} from '@chakra-ui/react';
import { useState, useRef, useEffect } from 'react';
import { firmarDocumento } from '../../services/documentos';
import { errorNotification, okNotification } from '../../utils/Notificaciones';

// Configuración de firmas requeridas por tipo de documento
const FIRMAS_POR_TIPO = {
	HOJA_DE_VISITA: [
		{ field: 'clientSignature', label: 'Firma del Cliente' },
		{ field: 'agentSignature', label: 'Firma del Agente' },
	],
	COMPROMISO_DE_VENTA: [
		{ field: 'sellerSignature', label: 'Firma del Vendedor' },
		{ field: 'buyerSignature', label: 'Firma del Comprador' },
	],
	ANEXO_COMPROMISO_DE_VENTA: [
		{ field: 'firmaUno', label: 'Firma 1', optional: false },
		{ field: 'firmaDos', label: 'Firma 2', optional: true },
		{ field: 'firmaTres', label: 'Firma 3', optional: true },
		{ field: 'firmaCuatro', label: 'Firma 4', optional: true },
	],
	CONTRATOINTERMEDACIONWOLFIN: [
		{ field: 'firmaAgente', label: 'Firma del Agente', optional: false },
		{ field: 'firmaClienteUno', label: 'Firma Cliente 1', optional: false },
		{ field: 'firmaClienteDos', label: 'Firma Cliente 2', optional: true },
	],
	DECLARACION_DE_COMISION: [
		{ field: 'firmaUno', label: 'Firma 1', optional: false },
		{ field: 'firmaDos', label: 'Firma 2', optional: true },
		{ field: 'firmaAgente', label: 'Firma Agente', optional: false },
	],
	GASTOS_VENDEDOR: [
		{ field: 'sellerSignature', label: 'Firma del Vendedor' },
	],
	GASTOS_COMPRADOR: [
		{ field: 'buyerSignature', label: 'Firma del Comprador' },
	],
	PROPUESTA_DE_COMPRAVENTA: [
		{ field: 'buyerSignature', label: 'Firma del Comprador' },
		{ field: 'agentSignature', label: 'Firma del Agente' },
	],
	RECISION_DE_CONTRATO: [
		{ field: 'partySignature', label: 'Firma de la Parte' },
		{ field: 'agencySignature', label: 'Firma de la Agencia' },
	],
	RECONOCIMIENTO_DE_HONORARIOS: [
		{ field: 'clientSignature', label: 'Firma del Cliente' },
		{ field: 'agencySignature', label: 'Firma de la Agencia' },
	],
};

const SignatureCanvas = ({ onSignatureChange, signatureData }) => {
	const canvasRef = useRef(null);
	const [isDrawing, setIsDrawing] = useState(false);

	const startDrawing = e => {
		const canvas = canvasRef.current;
		const rect = canvas.getBoundingClientRect();
		const ctx = canvas.getContext('2d');

		ctx.strokeStyle = '#000000';
		ctx.lineWidth = 2;
		ctx.lineCap = 'round';

		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		ctx.beginPath();
		ctx.moveTo(x, y);
		setIsDrawing(true);
	};

	const draw = e => {
		if (!isDrawing) return;

		const canvas = canvasRef.current;
		const rect = canvas.getBoundingClientRect();
		const ctx = canvas.getContext('2d');

		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		ctx.lineTo(x, y);
		ctx.stroke();
	};

	const stopDrawing = () => {
		if (isDrawing) {
			const canvas = canvasRef.current;
			const signatureBase64 = canvas.toDataURL('image/png');
			onSignatureChange(signatureBase64);
			setIsDrawing(false);
		}
	};

	const clearCanvas = () => {
		const canvas = canvasRef.current;
		const ctx = canvas.getContext('2d');
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		onSignatureChange(null);
	};

	return (
		<Box>
			<Box
				border='2px solid'
				borderColor='gray.300'
				borderRadius='md'
				bg='white'
				position='relative'
			>
				<canvas
					ref={canvasRef}
					width={500}
					height={200}
					onMouseDown={startDrawing}
					onMouseMove={draw}
					onMouseUp={stopDrawing}
					onMouseLeave={stopDrawing}
					style={{ cursor: 'crosshair', display: 'block' }}
				/>
			</Box>
			<Button size='sm' mt={2} onClick={clearCanvas} colorScheme='gray'>
				Limpiar
			</Button>
		</Box>
	);
};

const FirmarDocumentoModal = ({ isOpen, onClose, documento, onSuccess }) => {
	const [signatures, setSignatures] = useState({});
	const [isSigning, setIsSigning] = useState(false);
	const toast = useToast();

	// Reset signatures when modal opens or document changes
	useEffect(() => {
		if (isOpen && documento) {
			setSignatures({});
		}
	}, [isOpen, documento]);

	const handleSignatureChange = (field, signatureData) => {
		setSignatures(prev => ({
			...prev,
			[field]: signatureData,
		}));
	};

	const handleFirmar = async () => {
		if (!documento) {
			errorNotification('No hay documento para firmar', toast);
			return;
		}

		const firmasRequeridas = FIRMAS_POR_TIPO[documento.tipo] || [];

		// Solo validar firmas no opcionales
		const firmasFaltantes = firmasRequeridas.filter(
			firma => !firma.optional && !signatures[firma.field],
		);

		if (firmasFaltantes.length > 0) {
			errorNotification(
				`Faltan firmas obligatorias: ${firmasFaltantes.map(f => f.label).join(', ')}`,
				toast,
			);
			return;
		}

		try {
			setIsSigning(true);

			// Filtrar solo las firmas que tienen datos (no null)
			const firmasConDatos = Object.fromEntries(
				Object.entries(signatures).filter(
					([_, value]) => value !== null && value !== undefined,
				),
			);

			if (Object.keys(firmasConDatos).length === 0) {
				errorNotification('Debes firmar al menos una firma', toast);
				return;
			}

			await firmarDocumento(documento.uuid, firmasConDatos);

			okNotification('Documento firmado correctamente', toast);
			setSignatures({});
			onClose();
			onSuccess && onSuccess();
		} catch (error) {
			console.error('Error firmando documento:', error);
			errorNotification(
				error?.data?.message || 'Error al firmar el documento',
				toast,
			);
		} finally {
			setIsSigning(false);
		}
	};

	if (!documento) return null;

	const firmasRequeridas = FIRMAS_POR_TIPO[documento.tipo] || [];

	return (
		<Modal isOpen={isOpen} onClose={onClose} size='2xl'>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>Firmar Documento</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<VStack spacing={4} align='stretch'>
						<Text fontWeight='bold'>
							Tipo: {documento.tipo.replace(/_/g, ' ')}
						</Text>
						<Text fontSize='sm' color='gray.600'>
							Dibuja las firmas requeridas en los espacios a
							continuación
						</Text>

						{firmasRequeridas.length > 1 ? (
							<Tabs>
								<TabList>
									{firmasRequeridas.map(firma => (
										<Tab key={firma.field}>
											{firma.label}
											{firma.optional && (
												<Text
													as='span'
													fontSize='xs'
													ml={1}
													color='gray.500'
												>
													(opcional)
												</Text>
											)}
										</Tab>
									))}
								</TabList>

								<TabPanels>
									{firmasRequeridas.map(firma => (
										<TabPanel key={firma.field}>
											<SignatureCanvas
												onSignatureChange={data =>
													handleSignatureChange(
														firma.field,
														data,
													)
												}
												signatureData={
													signatures[firma.field]
												}
											/>
										</TabPanel>
									))}
								</TabPanels>
							</Tabs>
						) : (
							firmasRequeridas.map(firma => (
								<Box key={firma.field}>
									<Text fontWeight='medium' mb={2}>
										{firma.label}
										{firma.optional && (
											<Text
												as='span'
												fontSize='sm'
												ml={2}
												color='gray.500'
											>
												(opcional)
											</Text>
										)}
									</Text>
									<SignatureCanvas
										onSignatureChange={data =>
											handleSignatureChange(
												firma.field,
												data,
											)
										}
										signatureData={signatures[firma.field]}
									/>
								</Box>
							))
						)}
					</VStack>
				</ModalBody>

				<ModalFooter>
					<Button variant='ghost' mr={3} onClick={onClose}>
						Cancelar
					</Button>
					<Button
						colorScheme='green'
						onClick={handleFirmar}
						isLoading={isSigning}
					>
						Firmar Documento
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export default FirmarDocumentoModal;
